import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Search, Clock, Users, ChefHat, Star, Filter, Plus, UtensilsCrossed } from "lucide-react";

const SORT_LABELS = { featured: "Featured", popular: "Popular", rating: "Highest rated", new: "Newest", quick: "Quickest" };

function StarRow({ avg = 0, count = 0 }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs" title={`${avg.toFixed(1)} (${count})`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={11} strokeWidth={2}
          style={{ color: i <= Math.round(avg) ? "#c47f3a" : "#c8c1af", fill: i <= Math.round(avg) ? "#c47f3a" : "transparent" }} />
      ))}
      <span style={{ color: "var(--wofy-text-soft)" }}>{count > 0 ? `${avg.toFixed(1)} · ${count}` : "New"}</span>
    </span>
  );
}

function Card({ r }) {
  const totalMin = r.total_time_min || (r.prep_time_min + r.cook_time_min);
  const blurb = (() => {
    const src = r.ai_summary || r.short_description || "";
    if (!src) return "";
    if (src.length <= 150) return src;
    // Cut at a natural word boundary near 150 chars for a tidier ellipsis.
    const cut = src.slice(0, 150);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
  })();
  return (
    <Link
      to={`/recipes/${r.slug}`}
      data-testid={`recipe-card-${r.id}`}
      className="flex flex-col rounded-lg overflow-hidden border transition hover:shadow-lg hover:-translate-y-0.5"
      style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-cream, #FDFBF7)" }}
    >
      <div className="w-full aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center overflow-hidden">
        {r.cover_photo ? (
          <img src={r.cover_photo} alt={r.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <UtensilsCrossed size={48} style={{ color: "var(--wofy-primary)" }} />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1 gap-2">
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
          <span>{r.cuisine}</span>
          <span>·</span>
          <span>{r.country}</span>
          {r.featured && <span className="ml-auto px-2 py-0.5 rounded-full font-bold" style={{ background: "#c47f3a", color: "white" }}>FEATURED</span>}
        </div>
        <h3 className="font-display text-lg leading-snug">{r.title}</h3>
        <p className="text-xs leading-relaxed line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
          {blurb}
        </p>
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {totalMin || 0} min</span>
          <span className="inline-flex items-center gap-1"><Users size={11} /> {r.servings || 4}</span>
          <span className="inline-flex items-center gap-1"><ChefHat size={11} /> {r.difficulty}</span>
          <StarRow avg={r.rating_avg || 0} count={r.rating_count || 0} />
        </div>
        {r.diet_flags && r.diet_flags.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {r.diet_flags.slice(0, 3).map((f) => (
              <span key={f} className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(92,107,82,0.15)", color: "#5C6B52" }}>{f.replace("_", " ")}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Recipes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    q: searchParams.get("q") || "",
    category: searchParams.get("category") || "all",
    cuisine: searchParams.get("cuisine") || "all",
    diet: searchParams.get("diet") || "",
    difficulty: searchParams.get("difficulty") || "all",
    max_time: searchParams.get("max_time") || "",
    sort: searchParams.get("sort") || "featured",
  }), [searchParams]);

  useEffect(() => { http.get("/recipes/config").then((r) => setConfig(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 60, sort: filters.sort };
    if (filters.q) params.q = filters.q;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.cuisine !== "all") params.cuisine = filters.cuisine;
    if (filters.diet) params.diet = filters.diet;
    if (filters.difficulty !== "all") params.difficulty = filters.difficulty;
    if (filters.max_time) params.max_time = Number(filters.max_time);
    http.get("/recipes", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "all") next.delete(key); else next.set(key, val);
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="recipes-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {/* Hero */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <UtensilsCrossed size={13} className="inline mr-1.5 -mt-0.5" />
            RECIPES OF THE DAY
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">The kitchen of the world.</h1>
          <p className="text-sm mt-2 max-w-[52ch]" style={{ color: "var(--wofy-text-soft)" }}>
            A permanent, growing collection of family, cultural, and modern recipes shared by our community.
            Every recipe preserves the people, traditions, and stories behind it.
          </p>
        </div>
        <Link
          data-testid="recipe-submit-btn"
          to="/recipes/submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
          style={{ background: "var(--wofy-primary)", color: "white" }}
        >
          <Plus size={14} /> Share a recipe
        </Link>
      </div>

      {/* Search bar */}
      <div className="mb-6 flex items-center gap-2 border rounded-full px-4 py-3" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-cream,#FDFBF7)" }}>
        <Search size={16} style={{ color: "var(--wofy-text-soft)" }} />
        <input
          data-testid="recipe-search-input"
          type="text"
          placeholder="Search recipes, ingredients, cuisines, or countries…"
          defaultValue={filters.q}
          onKeyDown={(e) => { if (e.key === "Enter") setFilter("q", e.currentTarget.value); }}
          onBlur={(e) => setFilter("q", e.currentTarget.value)}
          className="flex-1 bg-transparent outline-none text-sm"
        />
      </div>

      {/* Filter row */}
      {config && (
        <div className="mb-6 flex flex-wrap gap-3 items-center text-sm">
          <Filter size={14} style={{ color: "var(--wofy-text-soft)" }} />
          <select data-testid="recipe-filter-cat" className="border rounded px-3 py-1.5 text-sm" value={filters.category} onChange={(e) => setFilter("category", e.target.value)}>
            <option value="all">All categories</option>
            {config.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="recipe-filter-cuisine" className="border rounded px-3 py-1.5 text-sm" value={filters.cuisine} onChange={(e) => setFilter("cuisine", e.target.value)}>
            <option value="all">All cuisines</option>
            {config.cuisines.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="recipe-filter-diff" className="border rounded px-3 py-1.5 text-sm" value={filters.difficulty} onChange={(e) => setFilter("difficulty", e.target.value)}>
            <option value="all">All levels</option>
            {config.difficulties.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="recipe-filter-diet" className="border rounded px-3 py-1.5 text-sm" value={filters.diet} onChange={(e) => setFilter("diet", e.target.value)}>
            <option value="">Any diet</option>
            {config.diet_flags.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <select data-testid="recipe-filter-time" className="border rounded px-3 py-1.5 text-sm" value={filters.max_time} onChange={(e) => setFilter("max_time", e.target.value)}>
            <option value="">Any time</option>
            <option value="15">≤ 15 min</option>
            <option value="30">≤ 30 min</option>
            <option value="60">≤ 1 h</option>
            <option value="120">≤ 2 h</option>
          </select>
          <select data-testid="recipe-filter-sort" className="border rounded px-3 py-1.5 text-sm ml-auto" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)}>
            {Object.entries(SORT_LABELS).map(([k, v]) => <option key={k} value={k}>Sort: {v}</option>)}
          </select>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading recipes…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border rounded-lg" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
          No recipes match these filters yet. Adjust the search, or <Link className="underline" to="/recipes/submit" style={{ color: "var(--wofy-primary)" }}>share the first one</Link>.
        </div>
      ) : (
        <>
          <div className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>{total} recipes</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {items.map((r) => (<Card key={r.id} r={r} />))}
          </div>
        </>
      )}
    </div>
  );
}
